import React, { useCallback, useEffect } from "react";
import { withFormik } from "formik";
import { isFunction, noop } from "lodash";
import { bool, func } from "prop-types";

import ContactFormSchema from "src/shared/utilities/contactFormSchema";
import { useCurrentUser } from "src/shared/utilities/customHooks";
import useIsMobileDeviceSize from "src/shared/utilities/state/apollo/hooks/useIsMobileDeviceSize";

import {
  HDP_LISTING_AGENT_FLOW,
  HDP_MESSAGE_SCREENING_FLOW
} from "../../constants/flows";
import {
  CONFIRMATION_MODAL_STEP,
  CONTACT_FORM_STEP,
  MESSAGE_SCREENING_STEP,
  POST_SUBMIT_QUESTIONNAIRE_STEP
} from "../../constants/steps";
import determineConfirmationScenario from "../../utilities/determineConfirmationScenario";
import getDrawerTitle from "../../utilities/getDrawerTitle";
import getInitialFormValues from "../../utilities/getInitialFormValues";
import useActiveExperts from "../../utilities/hooks/useActiveExperts";
import useContactExpert from "../../utilities/hooks/useContactExpert";
import useFeatureState from "../../utilities/hooks/useFeatureState";
import useFlow from "../../utilities/hooks/useFlow";
import useMessageFlagsQuery from "../../utilities/hooks/useMessageFlagsQuery";
import useUserProfileQuery from "../../utilities/hooks/useUserProfileQuery";
import triggerConfirmationDialog from "../../utilities/triggerConfirmationDialog";
import useReducerActions from "../../utilities/useReducerActions";
import ErrorState from "../ErrorState";
import LoadingState from "../LoadingState";
import ContactForm from "../NewContactForm";
import MessageScreening from "../NewMessageScreening";
import PostSubmitQuestionnaire from "../NewPostSubmitQuestionnaire";
import * as S from "./styled";

const SidePanelForm = ({ isOpen, onClose, onSubmit, ...formikProps }) => {
  const {
    resetForm: resetFormikForm,
    setValues: setFormikValues,
    values: formikValues,
    ...formikBag
  } = formikProps;

  const {
    building,
    expert,
    listing,
    loading: activeExpertLoading
  } = useActiveExperts();
  const onHdpPage = Boolean(listing);
  const onExpertsSubmit = useContactExpert();
  const currentUser = useCurrentUser();
  const { contactFormType, listingId } = useFeatureState();
  const isMobileDeviceSize = useIsMobileDeviceSize();
  const getMessageFlags = useMessageFlagsQuery();
  const { dispatchSetMessageScreeningFlag } = useReducerActions();
  const { loading: userProfileLoading } = useUserProfileQuery();

  const [
    { currentFlow, currentStep },
    { advanceStep, resetFlow, resetStep, setFlow }
  ] = useFlow();

  const setInitialFormikValues = useCallback(
    () =>
      setFormikValues(
        getInitialFormValues({
          building,
          contactFormType,
          currentUser,
          expert,
          listing
        })
      ),
    [building, currentUser, expert, listing]
  );

  useEffect(() => {
    setInitialFormikValues();
  }, [building, currentUser, expert, listing]);

  const triggerConfirmation = ({ email }) => {
    const {
      confirmationHeader,
      confirmationText
    } = determineConfirmationScenario({
      building,
      contactFormType,
      currentFlow,
      expert,
      listing
    });

    return triggerConfirmationDialog({
      confirmationHeader,
      confirmationText,
      email,
      origin: "contact_agent"
    });
  };

  const onFormSubmit = async () => {
    if (onSubmit && isFunction(onSubmit)) {
      await onSubmit();
    }

    triggerConfirmation(formikValues);

    resetFormikForm();
    setInitialFormikValues();
    resetFlow();
    resetStep();
  };

  useEffect(() => {
    (async () => {
      if (currentStep === CONFIRMATION_MODAL_STEP) {
        await onFormSubmit();
      }
    })();
  }, [currentStep]);

  const onContactFormSubmit = async (values, { setFieldError }) => {
    let result;

    if (onHdpPage) {
      result = await getMessageFlags(values);

      if (result.showMessageScreening) {
        // TODO FOS-1638: try moving logic into hook
        setFlow(HDP_MESSAGE_SCREENING_FLOW);
        dispatchSetMessageScreeningFlag(result.flag);
      } else if (result.error) {
        setFieldError("form_submission", "Failed to deliver message");
        return;
      } else {
        result = await onExpertsSubmit(values, { setFieldError });

        if (result.error) return;
      }
    } else {
      result = await onExpertsSubmit(values, { setFieldError });

      if (result.error) return;
    }

    advanceStep();
  };

  const isLoading =
    activeExpertLoading || userProfileLoading || !building || !currentUser;

  const isOnFirstStep =
    currentStep === CONTACT_FORM_STEP && !isLoading && expert;

  const headline = getDrawerTitle({
    hasListing: Boolean(listingId),
    isModal: isMobileDeviceSize,
    type: contactFormType
  });

  const steps = {
    [CONTACT_FORM_STEP]: {
      component: <ContactForm listing={listing} />,
      primaryActionButton: (
        <S.PrimaryActionButton
          onClick={async () => {
            await onContactFormSubmit(formikValues, formikBag);
          }}
        >
          Send message
        </S.PrimaryActionButton>
      ),
      secondaryActionButton: (
        <S.SecondaryActionButton onClick={onClose}>
          Cancel
        </S.SecondaryActionButton>
      )
    },
    [MESSAGE_SCREENING_STEP]: {
      component: (
        <MessageScreening
          onSubmit={advanceStep}
          switchFlow={() => setFlow(HDP_LISTING_AGENT_FLOW)}
        />
      )
    },
    [POST_SUBMIT_QUESTIONNAIRE_STEP]: {
      component: (
        <PostSubmitQuestionnaire
          agentName={expert?.contact.name}
          onSubmit={advanceStep}
        />
      )
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (!expert) {
      return <ErrorState />;
    }

    return steps[currentStep]?.component;
  };

  return (
    <S.SidePanel
      headline={isOnFirstStep ? headline : ""}
      isOpen={isOpen}
      onRequestClose={onClose}
      primaryActionButton={
        isOnFirstStep ? steps[currentStep]?.primaryActionButton : null
      }
      secondaryActionButton={
        isOnFirstStep ? steps[currentStep]?.secondaryActionButton : null
      }
    >
      {renderContent()}
    </S.SidePanel>
  );
};

SidePanelForm.propTypes = {
  isOpen: bool,
  onClose: func,
  onSubmit: func
};

SidePanelForm.defaultProps = {
  isOpen: false,
  onClose: noop,
  onSubmit: noop
};

const EnhancedSidePanelForm = withFormik({
  handleSubmit: noop,
  mapPropsToValues: () => getInitialFormValues(),
  validateOnBlur: false,
  validateOnChange: false,
  validationSchema: ContactFormSchema
})(SidePanelForm);

export default EnhancedSidePanelForm;
